import React from "react";
import { motion } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

interface DetailsSectionProps {
  title: string;
  heading: string;
  description: string;
  logoSrc: string;
  iconSrc: string;
  className?: string;
  isTextLeft?: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({
  title,
  heading,
  description,
  logoSrc,
  iconSrc,
  className = "",
  isTextLeft = false,
}) => {
  return isTextLeft ? (
    <motion.div
      className={`py-10 ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.3 }}
      variants={staggerContainer}
    >
      {/* Background Title */}
      <motion.h2
        className="text-[#a371ff18] font-outline-2 text-5xl sm:text-6xl lg:text-[130px] font-bold uppercase leading-none text-right"
        variants={fadeInUp}
      >
        {title}
      </motion.h2>
      <div className="flex gap-3 flex-col lg:flex-row">
        {/* Text Section */}
        <motion.div
          className="flex-1"
          variants={fadeInLeft}
        >
          <motion.h3
            className="flex items-center gap-2 bg-[linear-gradient(90deg,rgba(127,81,213,0.5)_0%,rgba(127,81,213,0)_100%)] relative mb-7 py-5 sm:py-8 pl-20 text-3xl sm:text-[50px]  font-bold uppercase max-w-[630px]"
            variants={fadeInUp}
          >
            <div className="w-2.5 h-full bg-white absolute left-0" />
            {iconSrc && <img src={iconSrc} alt="icon" className="max-w-[58px]" />}
            {heading}
          </motion.h3>
          <motion.p
            className="text-[#D1D1D1] text-xl sm:text-2xl px-4 sm:pl-8 text-center sm:text-left items-center max-w-[700px]"
            variants={fadeInUp}
          >
            {description}
          </motion.p>
        </motion.div>
        {/* Image Section */}
        <motion.div
          className="flex-1 h-fit max-w-[800px] mx-5 sm:mx-20"
          variants={scaleIn}
        >
          <img className="w-full object-contain" src={logoSrc} alt={title} />
        </motion.div>
      </div>
    </motion.div>
  ) : (
    <motion.div
      className={`py-10 ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.3 }}
      variants={staggerContainer}
    >
      {/* Background Title */}
      <motion.h2
        className="text-[#a371ff18] font-outline-2 text-5xl sm:text-6xl lg:text-[130px] font-bold uppercase leading-none"
        variants={fadeInUp}
      >
        {title}
      </motion.h2>

      <div className="flex gap-3 flex-col-reverse lg:flex-row">
        {/* Image Section */}
        <motion.div
          className="flex-1 h-fit max-w-[800px] mx-5 sm:mx-20"
          variants={scaleIn}
        >
          <img className="w-full object-contain" src={logoSrc} alt={title} />
        </motion.div>

        {/* Text Section */}
        <motion.div
          className="flex-1"
          variants={fadeInRight}
        >
          <motion.h3
            className="flex items-center gap-2 bg-[linear-gradient(270deg,rgba(127,81,213,0.5)_0%,rgba(127,81,213,0)_100%)] relative mb-7 py-5 sm:py-8 justify-end pr-20 text-3xl sm:text-[50px] font-bold uppercase max-w-[630px] ml-auto"
            variants={fadeInUp}
          >
            <div className="w-2.5 h-full bg-white absolute right-0" />
            {iconSrc && <img src={iconSrc} alt="icon" />}
            {heading}
          </motion.h3>
          <motion.p
            className="sm:text-right text-[#D1D1D1] text-xl sm:text-2xl px-4 sm:pr-8 text-center items-center max-w-[700px] ml-auto"
            variants={fadeInUp}
          >
            {description}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DetailsSection;
